import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetSalesExecutiveByIdQuery } from "@generated";
import { SalesExecutiveForm } from "../SalesExecutiveForm/SalesExecutiveForm";
import { useSalesExecutiveMutations } from "../api";
import { emptySalesExecutiveForm } from "../types";
import type { SalesExecutiveFormValues } from "../types";

const FORM_ID = "sales-executive-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useSalesExecutiveMutations();
  const { data, isLoading, refetch } = useV2GetSalesExecutiveByIdQuery(
    {
      id: Number(id),
    },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<SalesExecutiveFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<SalesExecutiveFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptySalesExecutiveForm;
    return {
      name: attrs.name ?? "",
      title: attrs.title ?? "",
      phone: attrs.phone ?? "",
      email: attrs.email ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: SalesExecutiveFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update sales executive", err);
      setToast({ variant: "danger", text: "Could not save sales executive." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Sales Executive"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <SalesExecutiveForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
